import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue, useRecoilValueLoadable, useResetRecoilState, useSetRecoilState } from 'recoil';
import { DepositService, EnumAgreementStatus, EnumRegistrationStatus, VehicleService } from 'api';
import { getActiveAgreementByIdRequestIdState } from 'api/agreements/getActiveAgreementsForCustomer';
import getAgreementsAndVehicle, { getAgreementsRequestIdState } from 'api/agreements/getAgreementsAndVehicle';
import { isVehicleAlreadyRegisteredRequestId as isVehicleAlreadyRegisteredRequestIdAtom } from 'api/vehicles/isVehicleAlreadyRegistered';
import { isVehicleAwaitingShippingRequestId as isVehicleAwaitingShippingRequestIdAtom } from 'api/vehicles/isVehicleAwaitingShipping';
import useCurrentCustomer from 'hooks/customer/useCurrentCustomer';
import deliveryAddressAtom, { isAddressFormValid } from 'state/address';
import { agreementOption as agreementOptionAtom, chosenAgreement as chosenAgreementAtom, newAgreementName as newAgreementNameAtom } from 'state/agreement';
import { isSubscriptionPlanFormValid, selectedSubscriptionPlan } from 'state/subscriptionPlan';
import { registeredVehicle as registeredVehicleAtom, vehicleFormIdsToRegister, vehiclesToRegister as vehiclesToRegisterAtom } from 'state/vehicles';
import { getSubscriptionPlanId } from 'utils/mapping/mapSubscriptionPlan';
import checkLoadable from 'utils/recoil/checkLoadable';
export class OrderTagError extends Error {
  constructor(message, failedRegistrations) {
    super(message);
    this.name = 'OrderTagError';
    this.failedRegistrations = failedRegistrations;
  }
}
export default function useOrderTag() {
  const [isLoading, setIsLoading] = useState(false);
  const [vehiclesToRegister, setVehiclesToRegister] = useRecoilState(vehiclesToRegisterAtom);
  const setRegisteredVehicle = useSetRecoilState(registeredVehicleAtom);
  const chosenAgreement = useRecoilValue(chosenAgreementAtom);
  const newAgreementName = useRecoilValue(newAgreementNameAtom);
  const deliveryAddress = useRecoilValue(deliveryAddressAtom);
  const isAddressValid = useRecoilValue(isAddressFormValid);
  const subscriptionPlan = useRecoilValue(selectedSubscriptionPlan);
  const isSubscriptionPlanValid = useRecoilValue(isSubscriptionPlanFormValid);
  const setActiveAgreementRequestId = useSetRecoilState(getActiveAgreementByIdRequestIdState);
  const setIsVehicleAlreadyRegisteredRequestId = useSetRecoilState(isVehicleAlreadyRegisteredRequestIdAtom);
  const setIsVehicleAwaitingShippingRequestId = useSetRecoilState(isVehicleAwaitingShippingRequestIdAtom);
  const setAgreementRequestId = useSetRecoilState(getAgreementsRequestIdState);
  const resetVehiclesToRegister = useResetRecoilState(vehiclesToRegisterAtom);
  const resetAgreementOption = useResetRecoilState(agreementOptionAtom);
  const resetChosenAgreement = useResetRecoilState(chosenAgreementAtom);
  const resetNewAgreementName = useResetRecoilState(newAgreementNameAtom);
  const resetDeliveryAddress = useResetRecoilState(deliveryAddressAtom);
  const resetVehicleFormIdsToRegister = useResetRecoilState(vehicleFormIdsToRegister);
  const resetSelectedSubscriptionPlan = useResetRecoilState(selectedSubscriptionPlan);
  const {
    enqueueSnackbar
  } = useSnackbar();
  const {
    t
  } = useTranslation();
  const {
    customer
  } = useCurrentCustomer();
  const agreementsLoadable = useRecoilValueLoadable(getAgreementsAndVehicle(customer?.id));
  const {
    contents: agreements
  } = checkLoadable(agreementsLoadable);
  const resetState = () => {
    resetVehiclesToRegister();
    resetChosenAgreement();
    resetNewAgreementName();
    resetDeliveryAddress();
    resetAgreementOption();
    resetVehicleFormIdsToRegister();
    resetSelectedSubscriptionPlan();
    setActiveAgreementRequestId(prev => prev + 1);
    setIsVehicleAlreadyRegisteredRequestId(prev => prev + 1);
    setIsVehicleAwaitingShippingRequestId(prev => prev + 1);
    setAgreementRequestId(prev => prev + 1);
  };
  const orderTag = async newAgreement => {
    setIsLoading(true);
    try {
      if (newAgreement && agreements?.some(a => a.status === EnumAgreementStatus.Frozen)) {
        enqueueSnackbar(t('register_vehicle.error_agreement_frozen'), {
          variant: 'error',
          autoHideDuration: 10000
        });
        throw new OrderTagError('Agreement is frozen');
      }

      // Get the subscription plan ID from the selected plan
      const subscriptionPlanId = getSubscriptionPlanId(subscriptionPlan);

      // Add subscriptionPlanId to each vehicle
      const vehiclesWithSubscriptionPlan = vehiclesToRegister.map(vehicle => ({
        ...vehicle,
        subscriptionPlanId
      }));
      const body = {
        shippingAddress: deliveryAddress,
        vehicles: vehiclesWithSubscriptionPlan,
        customerId: customer?.id,
        setShippingAddressOnAgreement: deliveryAddress?.makeStandard
      };
      let result;
      if (chosenAgreement) {
        body.agreementId = chosenAgreement.id;
        result = await VehicleService.registerVehicles({
          body
        });
      } else {
        body.agreementName = newAgreementName;
        result = await VehicleService.registerVehiclesAndCreateAgreement({
          body
        });
      }
      if (result.registrationStatus !== EnumRegistrationStatus.DepositRequired) {
        setRegisteredVehicle(vehiclesToRegister[0]);
        resetState();
      }
      return result;
    } catch (error) {
      if (error instanceof OrderTagError) throw error;
      if (Array.isArray(error)) {
        setVehiclesToRegister(vehicles => vehicles.filter(v => error.includes(v?.registrationNumber)));
        error?.forEach(registrationNumber => enqueueSnackbar(t('error.tag_order', {
          vehicleRegNr: registrationNumber
        }), {
          variant: 'error'
        }));
        throw new OrderTagError('Some vehicles failed registration', error);
      }
      throw new OrderTagError('Unexpected error ordering tag');
    } finally {
      setIsLoading(false);
    }
  };
  const acceptDepositRegistration = async creditAssessmentId => {
    setIsLoading(true);
    try {
      await DepositService.acceptDeposit({
        customerId: customer?.id,
        creditAssessmentId
      });
      setRegisteredVehicle(vehiclesToRegister[0]);
      resetState();
    } finally {
      setIsLoading(false);
    }
  };
  const rejectDepositRegistration = async creditAssessmentId => {
    setIsLoading(true);
    try {
      await DepositService.rejectDeposit({
        customerId: customer?.id,
        creditAssessmentId
      });
      resetState();
    } finally {
      setIsLoading(false);
    }
  };
  const vehiclesUnique = vehiclesToRegister.every((v1, i) => v1 === null || vehiclesToRegister.findIndex(v2 => v1?.registrationNumber === v2?.registrationNumber && v1?.nationality === v2?.nationality) === i);
  const isOrderValid = !isLoading && vehiclesToRegister.every(v => v !== null) && isAddressValid && isSubscriptionPlanValid && (chosenAgreement || newAgreementName) && vehiclesUnique;
  return {
    orderTag,
    acceptDepositRegistration,
    rejectDepositRegistration,
    isLoading,
    resetState,
    isOrderValid,
    vehiclesUnique
  };
}